import { isOrganizationAdmin, type OrganizationRole } from "@mountayaapp/api";

/**
 * OrgSubpageTail is the trailing path segment of one per-organization subpage route
 * (the part after `/organizations/$organizationId/`). The switcher reads it off the
 * current pathname and the nav lists it, so both consume the same access table keyed
 * by this one union.
 */
export type OrgSubpageTail =
  | "members"
  | "billing"
  | "usage"
  | "preferences"
  | "ai"
  | "api-keys"
  | "security"
  | "profile";

/**
 * OrganizationSubpageRoute is a typed `to` literal for one organization subpage, so
 * the typed router keeps full route safety — a bare interpolated string would collapse
 * the param/search types to `never`.
 */
export type OrganizationSubpageRoute = `/organizations/$organizationId/${OrgSubpageTail}`;

/**
 * SUBPAGE_ROUTE_BY_TAIL maps each subpage tail to its typed `to` route literal. A total
 * record over OrgSubpageTail makes a missing or extra key a compile error, so the access
 * table and the routes stay one source: every consumer that resolves a tail to a route
 * goes through this map rather than interpolating the tail into a string.
 */
export const SUBPAGE_ROUTE_BY_TAIL: Record<OrgSubpageTail, OrganizationSubpageRoute> = {
  ai: "/organizations/$organizationId/ai",
  "api-keys": "/organizations/$organizationId/api-keys",
  billing: "/organizations/$organizationId/billing",
  members: "/organizations/$organizationId/members",
  preferences: "/organizations/$organizationId/preferences",
  profile: "/organizations/$organizationId/profile",
  security: "/organizations/$organizationId/security",
  usage: "/organizations/$organizationId/usage",
};

/**
 * canAccessOrgSubpage reports whether a role may ENTER a subpage — whether the route's
 * guard lets it through. This is the enforcement gate. Every subpage except api-keys is
 * enterable by every role (they render a read-only surface for a non-manager rather than
 * bouncing), so only api-keys narrows to owner/admin. The switcher consults this to
 * decide whether preserving the current tab under a new organization would bounce the
 * user, and the api-keys route guard enforces exactly this predicate.
 */
export const canAccessOrgSubpage = (tail: OrgSubpageTail, role: OrganizationRole): boolean => {
  if (tail === "api-keys") {
    return isOrganizationAdmin(role);
  }

  return true;
};

/**
 * isOrgSubpageVisibleInNav reports whether the sidenav should SHOW a subpage's link for
 * a role. This is the visibility gate, never looser than enterability: a tab the role
 * cannot enter is never shown, and usage is additionally hidden from non-admins (a
 * billing-manager concern) even though its route admits every role. Everything else is
 * shown to every role, since the read-only pages still surface their link.
 */
export const isOrgSubpageVisibleInNav = (tail: OrgSubpageTail, role: OrganizationRole): boolean => {
  if (tail === "usage") {
    return isOrganizationAdmin(role);
  }

  return canAccessOrgSubpage(tail, role);
};

/**
 * OrgSubpageViewer is who asks to enter or see a subpage: the role they hold in the
 * organization (null when they hold none) and whether they are Mountaya staff.
 */
export interface OrgSubpageViewer {
  readonly role: OrganizationRole | null;
  readonly staff: boolean;
}

/**
 * canViewerAccessOrgSubpage is the enforcement gate for any viewer: a role opens what that role
 * opens, and Mountaya staff open every subpage, so a staff member who is also a viewer reaches
 * the union of the two. Staff read every customer organization through the read waiver and
 * write nothing on it but the terms of its agreement, so each page renders read-only for them
 * where they do not manage it, api-keys included: they read the keys, and every key action is
 * absent. A viewer with neither opens nothing.
 */
export const canViewerAccessOrgSubpage = (
  tail: OrgSubpageTail,
  { role, staff }: OrgSubpageViewer,
): boolean => staff || (role !== null && canAccessOrgSubpage(tail, role));

/**
 * isOrgSubpageVisibleToViewer is the visibility gate for any viewer, never looser than the
 * enforcement gate above. Staff see every subpage, usage included (the monthly allowances it
 * shows are among the terms staff set) and api-keys too, which they read.
 */
export const isOrgSubpageVisibleToViewer = (
  tail: OrgSubpageTail,
  { role, staff }: OrgSubpageViewer,
): boolean => staff || (role !== null && isOrgSubpageVisibleInNav(tail, role));
